'use client';

import { useCallback, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, FileText, GripVertical, Plus, Search, X } from 'lucide-react';

import ItemSearchModal, { type FinanceItem, type ItemSelectionOptions } from '@/components/finance/modals/ItemSearchModal';
import type { ProposalLineItem } from '@/lib/proposals/proposalTypes';

const TABLE_INPUT_CLASS = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-900';
const TABLE_NUMERIC_INPUT_CLASS = `${TABLE_INPUT_CLASS} text-right font-mono`;
const TABLE_READONLY_CLASS = 'inline-flex min-h-[40px] w-full items-center justify-end rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200';
const TABLE_TEXTAREA_CLASS = `${TABLE_INPUT_CLASS} resize-none`;

interface ProposalLineItemEditorProps {
  lineItems: ProposalLineItem[];
  onChange: (items: ProposalLineItem[]) => void;
  currency?: string;
  isReadOnly?: boolean;
  defaultExpanded?: boolean;
  collapsible?: boolean;
  sectionTitle?: string;
  sectionDescription?: string;
  enableItemSearch?: boolean;
  itemSearchContactId?: number;
  itemSearchContactName?: string;
}

function buildProposalLineItemFromFinanceItem(item: FinanceItem, options: ItemSelectionOptions): ProposalLineItem {
  const quantity = Number(options.quantity) > 0 ? Number(options.quantity) : 1;
  const unitPrice = options.price_override ?? item.unit_price ?? 0;

  return {
    id: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    description: item.name,
    quantity,
    unit: item.unit_of_measure || 'unit',
    unit_price: unitPrice,
    line_total: quantity * unitPrice,
    notes: item.description || null,
    item_code: item.item_code || item.sku || item.barcode || null,
    category: item.category_name || null,
  };
}

export default function ProposalLineItemEditor({
  lineItems,
  onChange,
  currency = 'ZAR',
  isReadOnly = false,
  defaultExpanded = false,
  collapsible = true,
  sectionTitle = 'Line Items',
  sectionDescription,
  enableItemSearch = false,
  itemSearchContactId,
  itemSearchContactName,
}: ProposalLineItemEditorProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [itemSearchOpen, setItemSearchOpen] = useState(false);
  const isExpanded = collapsible ? expanded : true;

  const moveItem = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= lineItems.length || toIndex >= lineItems.length) {
      return;
    }

    const reordered = [...lineItems];
    const [movedItem] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, movedItem);
    onChange(reordered);
  }, [lineItems, onChange]);

  const updateItem = useCallback((index: number, field: keyof ProposalLineItem, value: ProposalLineItem[keyof ProposalLineItem]) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };

    if (field === 'quantity' || field === 'unit_price') {
      const qty = Number(updated[index].quantity) || 0;
      const price = Number(updated[index].unit_price) || 0;
      updated[index].line_total = qty * price;
    }

    onChange(updated);
  }, [lineItems, onChange]);

  const addItem = useCallback(() => {
    onChange([
      ...lineItems,
      {
        id: String(Date.now()),
        description: '',
        quantity: 1,
        unit: 'unit',
        unit_price: null,
        line_total: null,
      },
    ]);
  }, [lineItems, onChange]);

  const handleCatalogItemSelect = useCallback((item: FinanceItem, options: ItemSelectionOptions) => {
    onChange([
      ...lineItems,
      buildProposalLineItemFromFinanceItem(item, options),
    ]);
  }, [lineItems, onChange]);

  const removeItem = useCallback((index: number) => {
    onChange(lineItems.filter((_, itemIndex) => itemIndex !== index));
  }, [lineItems, onChange]);

  const subtotal = lineItems.reduce((sum, item) => sum + (item.line_total || 0), 0);
  const lineItemCountLabel = useMemo(
    () => `${lineItems.length} item${lineItems.length === 1 ? '' : 's'}`,
    [lineItems.length],
  );

  const renderEditorRows = () => (
    <div className="space-y-4">
      {lineItems.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/80 px-4 py-6 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-400">
          No line items have been added yet. Start with a scope item, deliverable, or pricing row.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900/40">
          <div className="hidden items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:border-gray-700 dark:bg-gray-900/80 dark:text-gray-400 lg:grid lg:grid-cols-[72px_minmax(0,1.9fr)_110px_110px_140px_140px_92px]">
            <span>Move</span>
            <span>Item</span>
            <span className="text-right">Qty</span>
            <span>Unit</span>
            <span className="text-right">Unit price</span>
            <span className="text-right">Line total</span>
            <span className="text-right">Actions</span>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {lineItems.map((item, index) => {
              const isDragged = draggedIndex === index;

              return (
                <div
                  key={item.id || `${item.description}-${index}`}
                  onDragOver={(event) => {
                    if (!isReadOnly) {
                      event.preventDefault();
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (!isReadOnly && draggedIndex !== null) {
                      moveItem(draggedIndex, index);
                    }
                    setDraggedIndex(null);
                  }}
                  onDragEnd={() => setDraggedIndex(null)}
                  className={`transition-colors ${isDragged
                    ? 'bg-blue-50/80 dark:bg-blue-900/20'
                    : 'bg-white hover:bg-gray-50/80 dark:bg-gray-900/30 dark:hover:bg-gray-900/60'
                    }`}
                >
                  <div className="grid grid-cols-1 gap-3 px-4 py-4 lg:grid-cols-[72px_minmax(0,1.9fr)_110px_110px_140px_140px_92px] lg:items-start">
                    <div className="flex items-center gap-2 lg:pt-1">
                      {!isReadOnly && (
                        <button
                          type="button"
                          draggable
                          onDragStart={() => setDraggedIndex(index)}
                          onDragEnd={() => setDraggedIndex(null)}
                          className="inline-flex h-10 w-10 cursor-grab items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-400 shadow-sm transition hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
                          title="Drag to reorder"
                        >
                          <GripVertical className="h-4 w-4" />
                        </button>
                      )}
                      <span className="inline-flex h-10 min-w-[2.5rem] items-center justify-center rounded-lg bg-gray-100 px-2 text-xs font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                        {index + 1}
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400 lg:sr-only">Description</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(event) => updateItem(index, 'description', event.target.value)}
                          placeholder="Item description"
                          disabled={isReadOnly}
                          className={TABLE_INPUT_CLASS}
                        />
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {item.item_code && (
                          <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            {item.item_code}
                          </span>
                        )}
                        {item.category && (
                          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                            {item.category}
                          </span>
                        )}
                      </div>

                      <div>
                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Notes</label>
                        <textarea
                          value={item.notes || ''}
                          onChange={(event) => updateItem(index, 'notes', event.target.value)}
                          placeholder="Optional detail, assumptions, or inclusions"
                          rows={2}
                          disabled={isReadOnly}
                          className={TABLE_TEXTAREA_CLASS}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400 lg:sr-only">Qty</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.quantity}
                        onChange={(event) => updateItem(index, 'quantity', Number(event.target.value))}
                        placeholder="0"
                        disabled={isReadOnly}
                        className={TABLE_NUMERIC_INPUT_CLASS}
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400 lg:sr-only">Unit</label>
                      <input
                        type="text"
                        value={item.unit || ''}
                        onChange={(event) => updateItem(index, 'unit', event.target.value)}
                        placeholder="each"
                        disabled={isReadOnly}
                        className={TABLE_INPUT_CLASS}
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400 lg:sr-only">Unit price</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price ?? ''}
                        onChange={(event) => updateItem(index, 'unit_price', Number(event.target.value))}
                        placeholder="0.00"
                        disabled={isReadOnly}
                        className={TABLE_NUMERIC_INPUT_CLASS}
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400 lg:sr-only">Line total</label>
                      <div className={TABLE_READONLY_CLASS}>
                        {currency} {(item.line_total || 0).toFixed(2)}
                      </div>
                    </div>

                    {!isReadOnly ? (
                      <div className="flex items-center justify-end gap-1 lg:pt-1">
                        <button
                          type="button"
                          onClick={() => moveItem(index, index - 1)}
                          disabled={index === 0}
                          className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                          title="Move up"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveItem(index, index + 1)}
                          disabled={index === lineItems.length - 1}
                          className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                          title="Move down"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300"
                          title="Remove line item"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!isReadOnly && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {enableItemSearch && (
            <button
              type="button"
              onClick={() => setItemSearchOpen(true)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-100 hover:text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
            >
              <Search className="h-4 w-4" />
              Add from item library
            </button>
          )}
          <button
            type="button"
            onClick={addItem}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-blue-300 bg-blue-50/70 px-3 py-3 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30"
          >
            <Plus className="h-4 w-4" />
            Add line item manually
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800/60">
      {collapsible ? (
        <>
          <button
            type="button"
            onClick={() => setExpanded((previous) => !previous)}
            className="flex w-full items-center justify-between bg-gray-50 px-4 py-3 transition-colors hover:bg-gray-100 dark:bg-gray-900/80 dark:hover:bg-gray-800"
          >
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {sectionTitle} ({lineItemCountLabel})
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {currency} {subtotal.toFixed(2)}
              </span>
            </div>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {isExpanded && (
            <div className="max-h-[34rem] overflow-y-auto p-4">
              {renderEditorRows()}
            </div>
          )}
        </>
      ) : (
        <div className="bg-white dark:bg-gray-800">
          <div className="border-b border-gray-200 bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/80">
            <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{sectionTitle}</h5>
                <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 shadow-sm dark:bg-gray-800 dark:text-gray-300">
                  {lineItemCountLabel}
                </span>
              </div>
              {sectionDescription && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{sectionDescription}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Current subtotal</p>
              <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{currency} {subtotal.toFixed(2)}</p>
            </div>
          </div>
          </div>
          <div className="p-5">
          {renderEditorRows()}
          </div>
        </div>
      )}

      {enableItemSearch && itemSearchOpen && (
        <ItemSearchModal
          isOpen={itemSearchOpen}
          onClose={() => setItemSearchOpen(false)}
          onSelect={handleCatalogItemSelect}
          documentType="sales"
          contactId={itemSearchContactId}
          contactName={itemSearchContactName}
          currency={currency}
        />
      )}
    </div>
  );
}
